// src/pages/health/ResultSchedule.js
import { useMemo, useState, useEffect } from "react";
import { addMonths, subMonths, startOfMonth, format, isSameDay } from "date-fns";
import { ko } from "date-fns/locale";
import Calendar from "react-calendar";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";                 // ✅ 추가
import { HealthMetricsAPI } from "../../../api/healthMetrics";    // ✅ 추가
import AuthCard from "../../../components/auth/AuthCard";
import BottomNav from "../../../components/common/BottomNav";
import { useLoadingGate } from "../../../hooks/useLoadingGate";

// ✅ 훅 임포트 전부 제거 (useCardioMonth 등)

// 타입 메타
const RESULT_TYPES = {
    cardio_endurance: { label: "심폐지구력" },
    triple_combo: { label: "3종 복합 운동" },
    strength: { label: "근력" },
    power: { label: "근파워" },
    muscular_endurance: { label: "근지구력" },
};

function makeDummySummary(monthStartDate, type) {
    const y = monthStartDate.getFullYear();
    const m = monthStartDate.getMonth();
    const base = [2, 5, 9, 13, 17, 21, 24, 27, 30].filter(
        (d) => new Date(y, m, d).getMonth() === m
    );
    const seed = (type || "all").length;
    return base
        .map((d, i) => {
            const c = (i + seed) % 4;
            const count = c === 0 ? 0 : (i % 3) + 1;
            return { date: format(new Date(y, m, d), "yyyy-MM-dd"), count };
        })
        .filter((r) => r.count > 0);
}

export default function ResultSchedule() {
    const ready = useLoadingGate();
    const navigate = useNavigate();
    const { type } = useParams();
    const meta = RESULT_TYPES[type];
    const isValidType = !!meta;

    useEffect(() => {
        if (!isValidType) navigate("/workout", { replace: true });
    }, [isValidType, navigate]);

    const [selected, setSelected] = useState(new Date());
    const [month, setMonth] = useState(startOfMonth(new Date()));

    const goPrev = () => setMonth((m) => startOfMonth(subMonths(m, 1)));
    const goNext = () => setMonth((m) => startOfMonth(addMonths(m, 1)));

    const ym = format(month, "yyyy-MM");
    const { data: monthList = [], isFetching } = useQuery({
        queryKey: ["result", "month", type, ym],
        queryFn: () => {
            switch (type) {
                case "cardio_endurance":
                    return HealthMetricsAPI.cardio.listMonth(ym);
                case "triple_combo":
                    return HealthMetricsAPI.tripleStrength.listMonth(ym);
                case "strength":
                    return HealthMetricsAPI.strength.listMonth(ym);
                case "power":
                    return HealthMetricsAPI.power.listMonth(ym);
                case "muscular_endurance":
                    return HealthMetricsAPI.muscularEndurance.listMonth(ym);
                default:
                    return Promise.resolve([]);
            }
        },
        enabled: ready && isValidType,
    });

    const keyFromRow = (row) => {
        const dtStr = row?.dt ?? row?.date ?? row?.created_at ?? row?.createdAt;
        if (!dtStr) return null;
        const d = new Date(dtStr);
        if (isNaN(d)) return null;
        return format(d, "yyyy-MM-dd");
    };

    const toArr = (x) => (Array.isArray(x) ? x : Array.isArray(x?.items) ? x.items : Array.isArray(x?.results) ? x.results : []);

    const countMap = useMemo(() => {
        const map = new Map();

        if (isValidType) {
            toArr(monthList).forEach((row) => {
                const key = keyFromRow(row);
                if (!key) return;
                map.set(key, (map.get(key) ?? 0) + 1);
            });
            return map;
        }

        // fallback 더미
        const summary = makeDummySummary(month, type);
        summary.forEach(({ date, count }) => map.set(date, count));
        return map;
    }, [isValidType, monthList, month, type]);

    const selKey = format(selected, "yyyy-MM-dd");
    const selectedCount = countMap.get(selKey) ?? 0;

    if (!isValidType) return null;

    return (
        <main className="h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7 bg-white">
            {!ready ? (
                <div className="flex-1" />
            ) : (
                <AuthCard className="flex-1 w-full mx-auto" title={meta.label}>
                    <div className="flex flex-col flex-1 pt-3 pb-10">
                        <div className="w-full flex items-center justify-between pb-7">
                            <button type="button" onClick={goPrev} className="rounded-md active:scale-95" aria-label="이전 달">
                                <ChevronLeftIcon className="w-6 h-6 text-gray-400" />
                            </button>
                            <div className="font-extrabold text-center text-2xl md:text-3xl">
                                {format(month, "yyyy년 M월", { locale: ko })}
                            </div>
                            <button type="button" onClick={goNext} className="rounded-md active:scale-95" aria-label="다음 달">
                                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                            </button>
                        </div>

                        <div className="w-full items-center justify-center sc-cal px-1.5">
                            <Calendar
                                activeStartDate={month}
                                onActiveStartDateChange={({ activeStartDate }) => setMonth(startOfMonth(activeStartDate))}
                                view="month"
                                formatShortWeekday={(locale, date) => format(date, "EEE", { locale: ko })}
                                value={selected}
                                onClickDay={(d) => setSelected(d)}
                                formatDay={() => ""}
                                showNavigation={false}
                                locale="ko-KR"
                                calendarType="gregory"
                                tileClassName={({ date }) => {
                                    const base =
                                        "!flex !flex-col !items-center !justify-center !py-1 !min-h-[56px] md:!py-3 md:!min-h-[64px] focus:outline-none focus-visible:outline-none";
                                    return isSameDay(date, selected)
                                        ? `${base} !bg-white !text-black ring-2 ring-[#5F9BFF] rounded-xl `
                                        : base;
                                }}
                                tileContent={({ date, view }) => {
                                    if (view !== "month") return null;
                                    const key = format(date, "yyyy-MM-dd");
                                    const cnt = countMap.get(key) ?? 0;
                                    return (
                                        <div className="pointer-events-none leading-none flex flex-col items-center font-semibold text-sm md:text-base">
                                            <div className="text-lg md:text-xl">{format(date, "d")}</div>
                                            <div className={`mt-0.5 md:mt-1 w-2 h-2 rounded-full ${cnt > 0 ? "bg-[#5F9BFF]" : "opacity-0"}`} />
                                        </div>
                                    );
                                }}
                            />
                        </div>

                        <div className="w-full mt-6">
                            <button
                                type="button"
                                disabled={isFetching || selectedCount === 0}
                                className="w-full h-12 md:h-14 rounded-xl font-semibold bg-white border border-gray-200 disabled:opacity-50 text-[#5F9BFF] text-base md:text-lg"
                                onClick={() =>
                                    navigate(`/workout/day?date=${selKey}&type=${type}`, {
                                        state: { typeLabel: meta.label, type },
                                    })
                                }
                            >
                                {selectedCount > 0 ? `${meta.label} 기록 확인하기` : "이 날짜의 운동 기록이 없습니다"}
                            </button>
                        </div>
                    </div>
                </AuthCard>
            )}
            <BottomNav className="h-16" />
        </main>
    );
}
